import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, ChevronRight, Twitter, Facebook, Linkedin, Link2, Home } from "lucide-react";
import { toast } from "sonner";

const humanDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/posts/${slug}`)
      .then((r) => {
        setPost(r.data.post);
        setRelated(r.data.related || []);
      })
      .catch(() => { navigate("/blog"); })
      .finally(() => setLoading(false));
    // scroll to top on each new post
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, navigate]);

  // SEO tags — set on the document head. Uses server-provided values with
  // sensible fallbacks so every post has a useful preview even when the
  // admin didn't fill out the SEO section.
  useEffect(() => {
    if (!post) return;
    const seoTitle = post.seo_title || post.title;
    const metaDesc = post.meta_description || post.excerpt || "";
    const ogImage = post.og_image_url || post.cover_image_url || "";
    const canonical = post.canonical_url || `${window.location.origin}/blog/${post.slug}`;
    document.title = `${seoTitle} — Dream Home Engineers`;
    const set = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.split("[")[0]);
        const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    set('meta[name="description"]', "content", metaDesc);
    if (post.keywords?.length) set('meta[name="keywords"]', "content", post.keywords.join(", "));
    set('meta[property="og:title"]', "content", seoTitle);
    set('meta[property="og:description"]', "content", metaDesc);
    set('meta[property="og:type"]', "content", "article");
    if (ogImage) set('meta[property="og:image"]', "content", ogImage);
    set('meta[name="twitter:card"]', "content", ogImage ? "summary_large_image" : "summary");
    set('meta[name="twitter:title"]', "content", seoTitle);
    set('meta[name="twitter:description"]', "content", metaDesc);
    if (ogImage) set('meta[name="twitter:image"]', "content", ogImage);
    set('link[rel="canonical"]', "href", canonical);
    // Article JSON-LD for rich snippets.
    let ld = document.head.querySelector('#blog-jsonld');
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "blog-jsonld";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": metaDesc,
      "image": ogImage || undefined,
      "datePublished": post.published_at || post.created_at,
      "dateModified": post.updated_at || post.created_at,
      "author": { "@type": "Person", "name": post.author_name || "Dream Home Engineers" },
      "publisher": { "@type": "Organization", "name": "Dream Home Engineers" },
      "mainEntityOfPage": canonical,
    });
  }, [post]);

  // Sanitize HTML from the CMS before rendering — never trust admin input
  // to escape a stored-XSS injection. `dompurify` strips <script>, event
  // handlers, javascript: URIs, etc.
  const sanitized = useMemo(() => {
    if (!post?.body_html) return "";
    return DOMPurify.sanitize(post.body_html, {
      ALLOWED_TAGS: ["h1", "h2", "h3", "h4", "h5", "h6", "p", "a", "img", "ul", "ol", "li", "blockquote", "code", "pre", "em", "strong", "b", "i", "u", "br", "hr", "table", "thead", "tbody", "tr", "th", "td", "iframe", "video", "source"],
      ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "class", "colspan", "rowspan", "width", "height", "controls", "poster", "type", "loading", "decoding", "frameborder", "allow", "allowfullscreen"],
      ALLOW_UNKNOWN_PROTOCOLS: false,
    });
  }, [post?.body_html]);

  const share = (net) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(post.title);
    const map = {
      twitter:  `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer,width=640,height=520");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  if (loading) return <Layout><div className="max-w-3xl mx-auto py-24 text-center text-muted-foreground">Loading article…</div></Layout>;
  if (!post) return null;

  return (
    <Layout>
      <article className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-16">
        {/* Breadcrumb */}
        <nav data-testid="blog-breadcrumb" className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-8">
          <Link to="/" className="hover:text-foreground flex items-center gap-1"><Home size={11} /> Home</Link>
          <ChevronRight size={12} />
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <ChevronRight size={12} />
          {post.category && (
            <>
              <Link to={`/blog?category=${encodeURIComponent(post.category)}`} className="hover:text-foreground">{post.category}</Link>
              <ChevronRight size={12} />
            </>
          )}
          <span className="text-foreground truncate max-w-[280px]" title={post.title}>{post.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-4">
              {post.category && <Link to={`/blog?category=${encodeURIComponent(post.category)}`} className="overline text-accent hover:underline">{post.category}</Link>}
              {post.reading_time_minutes ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={11} />{post.reading_time_minutes} min read</span> : null}
            </div>
            <h1 data-testid="blog-post-title" className="font-display text-4xl lg:text-6xl leading-[0.98] tracking-tighter">{post.title}</h1>
            {post.excerpt && <p className="mt-6 text-lg text-muted-foreground">{post.excerpt}</p>}

            <div className="mt-6 flex items-center gap-4 pb-6 border-b border-border">
              <div className="flex items-center gap-3">
                {post.author_avatar && <img src={post.author_avatar} alt={post.author_name} className="w-10 h-10 rounded-full object-cover" />}
                <div>
                  <div className="text-sm font-semibold">{post.author_name || "Dream Home Engineers"}</div>
                  <div className="text-xs text-muted-foreground">{humanDate(post.published_at || post.created_at)}</div>
                </div>
              </div>
              <div className="ml-auto flex gap-1" data-testid="blog-share-buttons">
                <button onClick={() => share("twitter")} aria-label="Share on Twitter" className="p-2 border border-border hover:bg-neutral-100"><Twitter size={14} /></button>
                <button onClick={() => share("facebook")} aria-label="Share on Facebook" className="p-2 border border-border hover:bg-neutral-100"><Facebook size={14} /></button>
                <button onClick={() => share("linkedin")} aria-label="Share on LinkedIn" className="p-2 border border-border hover:bg-neutral-100"><Linkedin size={14} /></button>
                <button onClick={copyLink} aria-label="Copy link" data-testid="blog-copy-link" className="p-2 border border-border hover:bg-neutral-100"><Link2 size={14} /></button>
              </div>
            </div>

            {post.cover_image_url && (
              <div className="mt-8 aspect-[16/9] overflow-hidden bg-muted border border-border">
                <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" loading="eager" />
              </div>
            )}

            <div
              className="mt-10 prose max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-accent prose-img:rounded-none prose-code:before:content-none prose-code:after:content-none"
              data-testid="blog-post-body"
              dangerouslySetInnerHTML={{ __html: sanitized }}
            />

            {post.tags?.length > 0 && (
              <div className="mt-10 pt-6 border-t border-border">
                <div className="overline text-muted-foreground mb-2">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="text-xs px-2 py-1 bg-neutral-100 hover:bg-accent hover:text-neutral-900">#{t}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Related posts */}
          <aside className="lg:col-span-4">
            <div className="border border-border p-6 lg:sticky lg:top-24">
              <div className="overline text-accent">Related articles</div>
              <div className="mt-4 space-y-4" data-testid="blog-related-list">
                {related.length === 0 && <div className="text-sm text-muted-foreground">More articles coming soon.</div>}
                {related.map((r) => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="block group border-t border-border pt-4 first:border-t-0 first:pt-0">
                    {r.cover_image_url && (
                      <div className="aspect-video overflow-hidden bg-muted mb-3">
                        <img src={r.cover_image_url} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="text-xs uppercase tracking-widest text-accent">{r.category}</div>
                    <div className="mt-1 font-semibold text-sm group-hover:text-accent transition">{r.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{humanDate(r.published_at || r.created_at)}</div>
                  </Link>
                ))}
              </div>
              <Button asChild className="rounded-none w-full mt-6 bg-neutral-900 hover:bg-neutral-800">
                <Link to="/blog"><ArrowLeft size={14} className="mr-2" /> All articles</Link>
              </Button>
            </div>
          </aside>
        </div>
      </article>
    </Layout>
  );
};

export default BlogPost;
